import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useEQSelector, useOutsideClick } from 'src/Hooks';
import { EQActionType, EQStateType, PointType } from '../..';
import { EQActionBtn } from '../../EQActionBtn/EQActionBtn';
import { EQueueType, WidgetType } from 'src/Types/ReDoc/EQueue';
import './EQWidget.scss';

interface IEQWidgetProps {
	state: EQStateType;
	type: EQueueType;
	onAction: (action: string) => void;
	onClose: () => void;
	position?: PointType | null;
}

interface EQWidgetTicketType {
	number?: string;
	description?: string;
	startServingTime?: string;
	startTime?: string;
}

/**
 * Виджет электронной очереди
 * Отображает текущее состояние очереди, информацию о посетителе и доступные действия
 * Управляет таймером времени обслуживания и позиционированием
 */
const EQWidget: React.FC<IEQWidgetProps> = ({
	type,
	position,
	state,
	onClose,
	onAction,
}) => {
	const pultStatus = useEQSelector<WidgetType>(type, 'widgetState');
	const ticket = useEQSelector<EQWidgetTicketType | undefined>(
		type,
		'ticket',
	);
	const queueCount = useEQSelector<number>(type, 'allCount');
	const pQueueCount = useEQSelector<number>(type, 'queuePersonCount');
	const modalRef = useOutsideClick<HTMLDivElement>(onClose);
	const timerRef = useRef<NodeJS.Timeout>();
	const [time, setTime] = useState<string>('');

	/** Следит за изменением состояния пульта. Закрывает виджет если состояние не Working или нет state */
	useEffect(() => {
		if (pultStatus !== WidgetType.Working || !state) {
			clearTimer();
			onClose();
			return;
		}
	}, [pultStatus, state]);

	/** Управляет таймером времени обслуживания. Запускает/останавливает таймер в зависимости от состояния Working */
	useEffect(() => {
		if (pultStatus === WidgetType.Working) {
			setTime(calcTime(ticket?.startServingTime || ticket?.startTime));
			if (!timerRef.current) {
				timerRef.current = setInterval(() => {
					const _time = calcTime(
						ticket?.startServingTime || ticket?.startTime,
					);
					setTime(_time);
				}, 1000);
			}
		} else {
			clearTimer();
		}

		return () => {
			if (timerRef.current) {
				clearInterval(timerRef.current);
			}
		};
	}, [pultStatus]);

	const styles = position ? { position: 'absolute', ...position } : {};
	const count = `Ожидают ${queueCount} человек`;
	const number = ticket?.number || '';

	/** Очищает таймер и сбрасывает время */
	const clearTimer = () => {
		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = undefined;
		}
		setTime('');
	};

	/** Формирует текст для отображения в зависимости от состояния. Возвращает title и name для разных состояний и типов очередей */
	const getText = () => {
		const texts = { title: '', name: '' };

		const isEnter = type === EQueueType.Enter;
		switch (pultStatus) {
			case WidgetType.Working: {
				texts.title = time;
				texts.name = ticket?.description || '';
				break;
			}
			case WidgetType.Calling: {
				texts.title = number;
				texts.name = ticket?.description || '';
				break;
			}
			case WidgetType.Waiting:
			case WidgetType.OnPause: {
				if (isEnter) {
					texts.title = number
						? number
						: `Персональная очередь - ${pQueueCount} ч.`;
					texts.name = count;
				} else {
					texts.title = count;
					texts.name = number;
				}
				break;
			}
		}
		return texts;
	};

	const texts = getText();
	const actions =
		type === EQueueType.Enter
			? state.actions?.filter((a) => {
					return a.name !== 'asc-redirect';
				})
			: type === EQueueType.Return
				? []
				: state?.actions;
	return (
		<div ref={modalRef} style={styles} className={'enter-eq__modal extend'}>
			{ModalHeader(state)}
			{ModalBody(texts.title, texts.name)}
			{!!actions && ModalToolbar(type, actions, onAction)}
		</div>
	);
};

/** Рендерит панель инструментов с кнопками действий */
const ModalToolbar = (
	type: EQueueType,
	actions: Array<EQActionType>,
	onClick: (action: string) => void,
) => {
	return (
		<div className="modal__toolbar">
			{actions.map((action) => {
				return (
					<EQActionBtn
						key={'eq_action_' + action.name}
						action={action}
						onClick={onClick}
					/>
				);
			})}
		</div>
	);
};

/** Рендерит тело модального окна. Отображает номер талона и описание */
const ModalBody = (title = '', name = '') => {
	return (
		<>
			{!!title && (
				<div className="modal__body">
					<h3 className="modal__body__ticket" title={title}>
						{title}
					</h3>
				</div>
			)}
			<div className="modal__body desc">
				<p className={'modal__body__desc'} title={name}>
					{name}
				</p>
			</div>
		</>
	);
};

/** Рендерит заголовок модального окна. Отображает текст состояния с цветом фона */
const ModalHeader = (state: EQStateType) => {
	return (
		<div className="modal__header" style={{ backgroundColor: state.color }}>
			<h4 className="modal__header__title">{state.text}</h4>
		</div>
	);
};

/** Вычисляет время обслуживания от начала до текущего момента */
const calcTime = (startServingTime?: string): string => {
	if (!startServingTime) {
		return '';
	}
	const now = moment.utc().milliseconds(0);
	const start = moment(startServingTime);
	const diff = moment.utc(now.diff(start));
	return diff.format('HH:mm:ss');
};

export default EQWidget;
